import { Bookmark, Maximize2, Minimize2, Pencil } from 'lucide-react';
import React, { useState } from 'react';

import { useExcalidrawPersistence } from '../../../hooks/useExcalidrawPersistence';
import ExcalidrawCanvas from '../../Common/ExcalidrawCanvas';
import MarkdownDisplay from '../../Common/MarkdownDisplay';
import MarkdownEditor from '../../Common/MarkdownEditor';
import ToggleButtonGroup from '../../Common/ToggleButtonGroup';
import { useTranslation } from 'react-i18next';
import { useCadrageStore } from '../../../stores/cadrageStore';
import { useLayoutStore } from '../../../stores/layoutStore';
import { cn } from '../../../lib/utils';

type ArchitectureView = 'diagram' | 'text';

interface CadrageArchitectureFlowSectionProps {
  cadrageId: string | undefined; // ID nécessaire pour le hook
  isReadOnly?: boolean; // Mode lecture seule
}

const CadrageArchitectureFlowSection: React.FC<CadrageArchitectureFlowSectionProps> = ({
  cadrageId,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const { isFullscreen, toggleFullscreen } = useLayoutStore();
  const {
    data,
    setData,
    isEditing,
    isLoading,
    error,
    toggleEditing,
    saveDiagram,
  } = useExcalidrawPersistence({ type: 'cadrage', id: cadrageId });
  const architectureNotesMd = useCadrageStore((state) => state.architectureNotesMd);
  const updateArchitectureNotesMd = useCadrageStore((state) => state.updateArchitectureNotesMd);
  const saveArchitectureNotes = useCadrageStore((state) => state.saveArchitectureNotes);

  const [view, setView] = useState<ArchitectureView>('diagram');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [notesSaveError, setNotesSaveError] = useState<string | null>(null);
  const [notesSaved, setNotesSaved] = useState(false);

  const handleNotesChange = (value: string | undefined) => {
    setNotesSaved(false);
    updateArchitectureNotesMd(value || '');
  };

  const handleSaveNotes = async () => {
    setIsSavingNotes(true);
    setNotesSaved(false);
    setNotesSaveError(null);
    try {
      await saveArchitectureNotes();
      setNotesSaved(true);
    } catch (err) {
      setNotesSaveError(err instanceof Error ? err.message : t('cadrage.architecture.unknownSaveError'));
    } finally {
      setIsSavingNotes(false);
    }
  };

  return (
    <div className={cn(
      "bg-card rounded-lg shadow-sm border border-border",
      isFullscreen && "fixed inset-0 z-50 rounded-none flex flex-col"
    )}>
      <div className="flex flex-wrap justify-between items-center gap-3 p-6 border-b border-border">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-medium text-card-foreground">
            {t('cadrage.architecture.title')}
          </h2>
          <ToggleButtonGroup<ArchitectureView>
            ariaLabel={t('cadrage.architecture.viewToggleLabel')}
            options={[
              { value: 'diagram', label: t('cadrage.architecture.viewDiagram') },
              { value: 'text', label: t('cadrage.architecture.viewText') },
            ]}
            value={view}
            onChange={setView}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
           {/* Bouton Plein écran */}
           <button
             onClick={toggleFullscreen}
             className="inline-flex items-center px-3 py-2 border border-border rounded-md shadow-sm text-sm font-medium text-foreground bg-background hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
             title={isFullscreen ? t('global.exitFullscreen') : t('global.fullscreen')}
           >
             {isFullscreen ? (
               <>
                 <Minimize2 className="h-4 w-4 mr-1" /> {t('global.exitFullscreen')}
               </>
             ) : (
               <>
                 <Maximize2 className="h-4 w-4 mr-1" /> {t('global.fullscreen')}
               </>
             )}
           </button>
           {/* Bouton Sauvegarder les notes (vue texte) */}
           {!isReadOnly && view === 'text' && (
             <button
               onClick={handleSaveNotes}
               disabled={isSavingNotes}
               className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
             >
               <Bookmark className="h-4 w-4 mr-1" />
               {isSavingNotes ? t('global.saving') : t('cadrage.architecture.saveNotes')}
             </button>
           )}
           {/* Bouton Sauvegarder (visible en édition) */}
           {!isReadOnly && view === 'diagram' && isEditing && (
             <button
               onClick={saveDiagram} // Appeler la fonction de sauvegarde du hook
               disabled={isLoading} // Désactiver pendant le chargement/sauvegarde
               className="inline-flex items-center px-3 py-2 border border-primary rounded-md shadow-sm text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
             >
               <Bookmark className="h-4 w-4 mr-1" /> {t('cadrage.architecture.saveDiagram')}
             </button>
           )}
           {/* Bouton Éditer/Verrouiller */}
           {!isReadOnly && view === 'diagram' && (
             <button
               onClick={toggleEditing} // Bascule le mode édition du diagramme
               className={`inline-flex items-center px-3 py-2 border shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                 isEditing
                   ? 'border-transparent text-primary-foreground bg-primary hover:bg-primary/90 focus:ring-primary'
                   : 'border-border text-foreground bg-background hover:bg-accent focus:ring-primary'
               }`}
             >
               {isEditing ? (
                 <>
                   <Bookmark className="h-4 w-4 mr-1" /> {t('cadrage.architecture.lockEditing')}
                 </>
               ) : (
                 <>
                   <Pencil className="h-4 w-4 mr-1" /> {t('cadrage.architecture.editMode')}
                 </>
               )}
             </button>
           )}
       </div>
     </div>

      {view === 'text' ? (
        <div className={cn(
          "p-4 overflow-y-auto",
          isFullscreen ? "flex-1 min-h-0" : "min-h-[600px]"
        )}>
          {notesSaveError && (
            <p role="alert" className="mb-3 text-sm text-destructive">
              {t('cadrage.architecture.saveNotesErrorPrefix')} {notesSaveError}
            </p>
          )}
          <p role="status" className="mb-3 text-sm text-muted-foreground empty:hidden">
            {notesSaved && t('cadrage.architecture.notesSaved')}
          </p>
          {isReadOnly ? (
            <MarkdownDisplay
              content={architectureNotesMd}
              emptyMessage={t('cadrage.architecture.notesEmpty')}
            />
          ) : (
            <MarkdownEditor
              value={architectureNotesMd}
              onChange={handleNotesChange}
              placeholder={t('cadrage.architecture.notesPlaceholder')}
              // Fills the screen below the section header in fullscreen.
              height={isFullscreen ? 'calc(100vh - 140px)' : 500}
            />
          )}
        </div>
      ) : (
        // Affichage du diagramme ou des messages d'état
        <div className={cn(
          "p-4 border border-border rounded-md overflow-hidden",
          isFullscreen ? "flex-1 min-h-0" : "h-[600px]"
        )}>
          {isLoading && (
            <p className="text-center text-muted-foreground">
              {t('cadrage.architecture.loadingDiagram')}
            </p>
          )}
          {error && (
            <p className="text-center text-destructive">
              {t('cadrage.architecture.errorPrefix')} {error}
            </p>
          )}
          {!isLoading && !error && (
            <ExcalidrawCanvas
              className="border rounded-md overflow-hidden h-full bg-background"
              initialData={data}
              readOnly={isReadOnly || !isEditing}
              onChange={setData}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default CadrageArchitectureFlowSection;
